import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useLang } from "@/i18n";
import api, { resolveMediaUrl } from "@/lib/api";
import BookingDialog from "@/components/BookingDialog";
import { MapPin, Star, Play } from "lucide-react";

export default function Hotels() {
  const { t, tt } = useLang();
  const [hotels, setHotels] = useState([]);

  useEffect(() => {
    api.get("/hotels").then((r) => setHotels(r.data)).catch(() => {});
  }, []);

  return (
    <div>
      <div className="section-warm py-16">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="text-xs uppercase tracking-[0.35em] text-brand-orange font-semibold mb-3">{t("nav.hotels")}</div>
          <h1 className="font-serif text-5xl md:text-6xl text-brand-navy" data-testid="hotels-title">{t("sections.hotels_title")}</h1>
          <p className="mt-3 text-slate-700 max-w-2xl">{t("sections.hotels_desc")}</p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-10 py-16">
        <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4" data-testid="hotels-grid">
        {hotels.map((h) => (
          <Link
            key={h.id}
            to={`/hotels/${h.id}`}
            data-testid={`hotels-list-card-${h.id}`}
            className="group bg-white rounded-2xl overflow-hidden border border-brand-gold/20 shadow-sm hover:shadow-xl transition"
          >
            <div className="aspect-[4/3] overflow-hidden">
              <img src={resolveMediaUrl(h.images?.[0])} alt={h.name} className="w-full h-full object-cover group-hover:scale-105 transition duration-700" />
            </div>
            <div className="p-6">
              <div className="text-xs uppercase tracking-widest text-brand-orange font-semibold">{h.city}</div>
              <h3 className="mt-2 font-serif text-2xl text-brand-navy">{h.name}</h3>
              <p className="mt-2 text-sm text-slate-600 line-clamp-3">{tt(h.description)}</p>
              <div className="mt-4 flex items-center gap-4 text-xs text-brand-navy/70">
                <span className="flex items-center gap-1"><MapPin size={12} /> {h.distance_to_haram}</span>
                <span className="flex items-center gap-1"><Star size={12} className="text-brand-gold" fill="currentColor" /> {h.rating}</span>
                {h.price_from > 0 && (
                  <span className="ms-auto font-serif text-brand-navy">${h.price_from}</span>
                )}
              </div>
            </div>
          </Link>
        ))}
      </div>
      </div>
    </div>
  );
}

export function HotelDetail() {
  const { id } = useParams();
  const { t, tt, lang } = useLang();
  const [hotel, setHotel] = useState(null);
  const [activeMedia, setActiveMedia] = useState(0);

  useEffect(() => {
    api.get(`/hotels/${id}`).then((r) => setHotel(r.data)).catch(() => {});
  }, [id]);

  if (!hotel) return <div className="max-w-7xl mx-auto px-6 py-24">Loading…</div>;

  const gallery = [
    ...(hotel.images || []).map((u) => ({ type: "photo", url: u })),
    ...(hotel.videos || []).map((u) => ({ type: "video", url: u })),
  ];
  const active = gallery[activeMedia];

  return (
    <div>
      {/* Hero */}
      <section className="relative">
        <div className="aspect-[16/9] max-h-[540px] overflow-hidden bg-brand-navy">
          {active?.type === "video" ? (
            <video src={resolveMediaUrl(active.url)} className="w-full h-full object-cover" autoPlay muted loop playsInline controls />
          ) : (
            <img src={resolveMediaUrl(active?.url)} alt={hotel.name} className="w-full h-full object-cover" />
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-brand-navy/85 via-brand-navy/20 to-transparent pointer-events-none" />
        </div>
        <div className="absolute bottom-0 left-0 right-0 pb-10 pointer-events-none">
          <div className="max-w-7xl mx-auto px-6 md:px-10 text-white">
            <div className="text-xs uppercase tracking-[0.35em] text-brand-gold mb-3">{hotel.city}</div>
            <h1 className="font-serif text-5xl md:text-6xl" data-testid="hotel-detail-title">{hotel.name}</h1>
            <div className="mt-3 flex items-center gap-4 text-sm text-canvas-cream/85">
              <span className="flex items-center gap-1"><MapPin size={14} /> {hotel.distance_to_haram}</span>
              <span className="flex items-center gap-1"><Star size={14} className="text-brand-gold" fill="currentColor" /> {hotel.rating}</span>
              {hotel.price_from > 0 && (
                <span className="font-serif text-lg">${hotel.price_from} <span className="text-xs opacity-70">/ {t("sections.from")}</span></span>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Media strip */}
      {gallery.length > 1 && (
        <section className="max-w-7xl mx-auto px-6 md:px-10 py-6 -mt-4">
          <div className="flex gap-3 overflow-x-auto pb-2" data-testid="hotel-media-strip">
            {gallery.map((g, i) => (
              <button
                key={i}
                onClick={() => setActiveMedia(i)}
                className={`relative rounded-lg overflow-hidden flex-shrink-0 w-32 h-20 border-2 ${i === activeMedia ? "border-brand-orange" : "border-transparent"}`}
                data-testid={`hotel-media-thumb-${i}`}
              >
                {g.type === "video" ? (
                  <>
                    <video src={resolveMediaUrl(g.url)} muted className="w-full h-full object-cover" />
                    <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                      <Play size={16} fill="white" className="text-white"/>
                    </div>
                  </>
                ) : (
                  <img src={resolveMediaUrl(g.url)} alt="" className="w-full h-full object-cover" />
                )}
              </button>
            ))}
          </div>
        </section>
      )}

      {/* Body */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 py-16 grid gap-12 md:grid-cols-3">
        <div className="md:col-span-2">
          <h2 className="font-serif text-3xl text-brand-navy mb-4">{{ tr: "Otel Hakkında", ar: "عن الفندق", en: "About the hotel", ru: "Об отеле" }[lang]}</h2>
          <p className="text-slate-700 leading-relaxed whitespace-pre-line">{tt(hotel.description)}</p>

          {tt(hotel.highlights) && (
            <div className="mt-8 bg-canvas-stone rounded-2xl p-6 border border-brand-gold/20">
              <h3 className="font-serif text-xl text-brand-navy mb-2">{{ tr: "Öne Çıkanlar", ar: "أبرز ما يميّزه", en: "Highlights", ru: "Особенности" }[lang]}</h3>
              <p className="text-slate-700 whitespace-pre-line">{tt(hotel.highlights)}</p>
            </div>
          )}

          {hotel.amenities?.length > 0 && (
            <div className="mt-8">
              <h3 className="font-serif text-xl text-brand-navy mb-3">{{ tr: "Olanaklar", ar: "المرافق", en: "Amenities", ru: "Удобства" }[lang]}</h3>
              <div className="flex flex-wrap gap-2">
                {hotel.amenities.map((a) => (
                  <span key={a} className="px-3 py-1 rounded-full bg-canvas-stone text-brand-navy text-sm border border-brand-gold/20">{a}</span>
                ))}
              </div>
            </div>
          )}

          {hotel.map_query && (
            <div className="mt-10">
              <h3 className="font-serif text-xl text-brand-navy mb-3">{t("sections.location_title")}</h3>
              <div className="aspect-[16/9] rounded-2xl overflow-hidden border border-brand-gold/30 shadow-sm">
                <iframe
                  title={`Map of ${hotel.name}`}
                  src={`https://www.google.com/maps?q=${encodeURIComponent(hotel.map_query)}&output=embed`}
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  data-testid="hotel-map-iframe"
                />
              </div>
            </div>
          )}
        </div>
        <div>
          <div className="bg-white rounded-2xl p-6 border border-brand-gold/20 shadow-sm sticky top-24">
            <div className="text-xs uppercase tracking-widest text-brand-orange font-semibold mb-2">{t("cta.inquire")}</div>
            {hotel.price_from > 0 && (
              <div className="font-serif text-3xl text-brand-navy mb-1">
                ${hotel.price_from}
                <span className="text-sm text-slate-500 font-sans"> / {t("sections.from")}</span>
              </div>
            )}
            <p className="text-sm text-slate-600 mb-4">{tt(hotel.description)?.slice(0, 120)}…</p>
            <BookingDialog defaultHotelId={hotel.id} />
          </div>
        </div>
      </section>
    </div>
  );
}
